/**
 * SearchBar: the Directory band's search field and its result count.
 *
 * The field is the house SearchField at its default size. Its accessible
 * name and placeholder follow the view: "Find a provider or service" for
 * the organizations view, "Search people" for the people view. On the map
 * it sits on the band, above the map's own "Find a client" box on the
 * map bar: two searches, two places, two names (LP-27). The query is
 * the caller's, and DirectoryHub keeps it in `?q=`.
 *
 * The band carries no search hero: a plain field on the page, with
 * category browsing in the body's "Browse by category" tiles. The
 * Directory turns the count line off (showCount false): its results say
 * "Showing N of M" beside Sort below the band.
 */

import type { ReactNode } from 'react'
import { PAGE_WIDTH_CLASSES, PAGE_GUTTER } from '@/components/primitives/PageShell'
import { SearchField } from '@/components/primitives/SearchField'

export interface SearchBarProps {
  query: string
  onQueryChange: (query: string) => void
  /** Null while the lists are loading or failed: no count is known. */
  resultCount: number | null
  hasFilters: boolean
  viewMode: string
  /** False on the Directory, whose results carry their own "Showing N of
   *  M" line below the band. */
  showCount?: boolean
}

/** The count line's words: "N results", "N contacts", with " (filtered)"
 *  while a filter is on; null while no count is known. */
function resultCountLabel(resultCount: number | null, hasFilters: boolean, viewMode: string): string | null {
  if (resultCount === null) return null
  const label = viewMode === 'people'
    ? `${resultCount.toLocaleString()} contact${resultCount !== 1 ? 's' : ''}`
    : `${resultCount.toLocaleString()} result${resultCount !== 1 ? 's' : ''}`
  return `${label}${hasFilters ? ' (filtered)' : ''}`
}

export function SearchBar({ query, onQueryChange, resultCount, hasFilters, viewMode, showCount = true }: SearchBarProps): ReactNode {
  const searchLabel = viewMode === 'people' ? 'Search people' : 'Find a provider or service'

  const resultLabel = resultCountLabel(resultCount, hasFilters, viewMode)

  return (
    <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER}`}>
      <SearchField
        value={query}
        onChange={onQueryChange}
        placeholder={searchLabel}
        aria-label={searchLabel}
        testId="directory-search"
      />

      {/* E1: sits directly on the sage-400 band → cream for legibility. */}
      {showCount && (
        <p className="mt-2.5 text-xs font-medium text-cream/90">
          {/* A non-breaking space keeps the line's height while no count is known. */}
          {resultLabel === null ? '\u00a0' : resultLabel}
        </p>
      )}
    </div>
  )
}
